import React, {useEffect, useRef, useState} from 'react'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import KTTable from 'helpers/ui-components/KTTable'
import KTPagination from 'helpers/ui-components/KTPagination'
import {
 ColumnProps,
 IFormData,
 IFormField,
} from 'models/Common'
import {KTSVG} from 'helpers'
import Spinner from 'layout/common/Spinner'
import {useLocation, useNavigate} from 'react-router-dom'
import {
 bulkUploadApi,
 fetchBulkUploadListApi,
} from 'utils/api/auth/bulkUploadService'
import {useListController} from 'hooks/useListController'

import DynamicModal from '../common/DynamicModal'
import {useTranslation} from 'react-i18next'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'

interface IBulkUpload {
 id: string
 date: string
 time: string
 totalCount: number
 successCount?: number
 failureCount?: number
 row_processing_progress?: string

 aggregator_org_name?: string
 aggregator_org_id?: number
 status:
  | 'COMPLETED'
  | 'PENDING'
  | 'FAILED'
  | 'PARTIALLY_COMPLETE'
  | 'ONGOING'
 result_file?: string
 parent_id?: number | null
 upload_type?: string
}

const BulkUploadManagement = () => {
 const [selectedFilter, setSelectedFilter] = useState('All')
 const navigate = useNavigate()
 const {t} = useTranslation()
 const [isPolling, setIsPolling] = useState(false)
 const [showDownloadModal, setShowDownloadModal] =
  useState(false)
 const [selectedRecord, setSelectedRecord] =
  useState<IBulkUpload | null>(null)
 const [loader, setLoader] = useState(false)
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const location = useLocation()
 const [showFailureModal, setShowFailureModal] =
  useState(false)
 const pollingRef = useRef(false)
 const [failureMessage, setFailureMessage] = useState('')
 const [filterFormData, setFilterFormData] =
  useState<IFormField>({})

 const [filterCount, setFilterCount] = useState(0)
 const filters = [
  'All',
  'Completed',
  'Partial Success',
  'Ongoing',
  'Failed',
 ]

 const formatDateTime = (dateString: string) => {
  const d = new Date(dateString)

  const date = `${d.toLocaleDateString('en-GB', {
   day: '2-digit',
  })}/${d.toLocaleDateString('en-GB', {
   month: 'short',
  })}/${d.getFullYear()}`

  const time = d.toLocaleTimeString('en-US', {
   hour: '2-digit',
   minute: '2-digit',
   hour12: true,
  })

  return {date, time}
 }

 const cleanPayload = (obj: IFormData) => {
  return Object.fromEntries(
   Object.entries(obj).filter(
    ([, value]) =>
     value !== undefined && value !== null && value !== ''
   )
  )
 }
 const getFilterConfigs = async () => {
  setLoader(true)

  const res = await fetchFilterConfig({
   filter_key: 'BULK_UPLOAD_LIST',
   filter_type: 'BULK_UPLOAD_LIST',
  })

  if (res) {
   setFilterConfig(res?.applicableFilters ?? [])
  }

  setLoader(false)
 }

 const handleDownload = () => {
  if (!selectedRecord?.result_file) return

  const link = document.createElement('a')
  link.href = selectedRecord.result_file
  link.download = ''
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setShowDownloadModal(false)
  setSelectedRecord(null)
 }

 const fetchBulkUploads = async (
  page: number,
  pageSize: number,
  query?: IFormField
 ) => {
  let finalParams: IFormData = {
   page,
   page_size: pageSize,
   upload_type: 'farmer-bulk-upload',
   ...(query || {}),
  }

  if (query?.search) {
   finalParams.aggregator_org_name = query.search
  }

  finalParams = cleanPayload(finalParams)

  const res = (await fetchBulkUploadListApi(
   finalParams
  )) as {
   results: IFormField[]
   count: number
  }

  const mappedData = (res.results || []).map((item) => {
   const {date, time} = formatDateTime(item.created_at)

   return {
    id: item.bulk_upload_id,
    date,
    aggregator_org_name: item.aggregator_org_name,
    aggregator_org_id: item.meta_data?.aggregator_org_id,
    time,
    totalCount: item.total_count,
    successCount: item.success_count,
    failureCount: item.failure_count,
    row_processing_progress: item.row_processing_progress,
    status: item.status,
    result_file: item.result_file,
    upload_type: item.upload_type,
   }
  })

  return {
   data: mappedData,
   total: res.count,
  }
 }

 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
  reload,
 } = useListController<IBulkUpload>({
  fetcher: fetchBulkUploads,
  initialPageSize: 10,
  setLoader,
 })

 const columns: Array<ColumnProps<IBulkUpload>> = [
  {
   key: 'sr',
   title: t('bulkUpload.columns.srNo'),
   className: 'text-center w-[80px]',
   render: (_, __, index?: number) =>
    totalRecord -
    ((currentPage - 1) * itemsPerPage + (index ?? 0)),
  },
  {
   key: 'aggregator_org_name',
   title: t('bulkUpload.columns.aggregator'),
  },
  {
   key: 'date',
   title: t('bulkUpload.columns.uploadedOn'),
   render: (_, record) => (
    <div>
     <p>{record.date}</p>
     <p className='text-xs text-[#7E8B96]'>{record.time}</p>
    </div>
   ),
  },
  {
   key: 'totalCount',
   title: t('bulkUpload.columns.totalCount'),
  },
  {
   key: 'successCount',
   title: t('bulkUpload.columns.successCount'),
   render: (_, record) => record.successCount ?? '-',
  },
  {
   key: 'failureCount',
   title: t('bulkUpload.columns.failureCount'),
   render: (_, record) => record.failureCount ?? '-',
  },
  {
   key: 'status',
   title: t('bulkUpload.columns.status'),
   render: (_, record) => {
    const colorMap = {
     COMPLETED: 'text-[#43A314]',
     PENDING: 'text-[#8C6500]',
     FAILED: 'text-[#CC293F]',
     PARTIALLY_COMPLETE: 'text-[#4C2C7A]',
     ONGOING: 'text-yellow',
    }

    const handleInlineDownload = () => {
     setSelectedRecord(record)
     setShowDownloadModal(true)
    }

    return (
     <div className='flex flex-col'>
      <span className={colorMap[record.status]}>
       {record.status === 'PARTIALLY_COMPLETE'
        ? 'PARTIALLY COMPLETE'
        : record.status}
      </span>

      {record.status === 'ONGOING' ? (
       <div className='flex items-center gap-1'>
        <img
         src='/media/logos/glassLoader.gif'
         alt='loading'
         className='w-6 h-6'
        />

        <span className='text-xs text-[#5A6066] whitespace-nowrap'>
         {record.row_processing_progress}
        </span>
       </div>
      ) : (
       record.result_file && (
        <span
         onClick={handleInlineDownload}
         className='flex items-center gap-1 text-[#4E83E4] text-xs cursor-pointer underline mt-1'
        >
         {t('bulkUpload.columns.logFile')}

         <KTSVG
          path='/media/logos/download.svg'
          className='w-3 h-3'
         />
        </span>
       )
      )}
     </div>
    )
   },
  },
  {
   key: 'action',
   title: t('bulkUpload.columns.action'),
   render: (_, record) => {
    return (
     <div className='flex justify-center'>
      {record.status !== 'COMPLETED' && (
       <button
        onClick={() => {
         navigate('bulk-upload-form', {
          state: {
           aggregatorName: record.aggregator_org_name,
           aggregatorId: record.aggregator_org_id,
          },
         })
        }}
        className='flex items-center gap-2 border border-[#E6EBF0] text-[#4E83E4] px-3 py-1 rounded-lg bg-white'
       >
        {t('bulkUpload.reUpload')}
       </button>
      )}
     </div>
    )
   },
  },
 ]

 const handleBulkUploadBtn = () => {
  navigate('bulk-upload-form')
 }

 const applyStatusFilter = (filter: string) => {
  setSelectedFilter(filter)

  const statusMap: IFormField = {
   Completed: 'COMPLETED',
   Pending: 'PENDING',
   Failed: 'FAILED',
   'Partial Success': 'PARTIALLY_COMPLETE',
   Ongoing: 'ONGOING',
  }

  const updatedFilters: IFormField = {
   ...filterFormData,
  }

  if (filter !== 'All') {
   updatedFilters.status = statusMap[filter]
  } else {
   updatedFilters.status = ''
  }

  setFilterFormData(updatedFilters)

  setCurrentPage(1)
 }

 useEffect(() => {
  void getFilterConfigs()
 }, [])

 useEffect(() => {
  const applyDynamicFilters = async () => {
   let finalPayload: IFormField = {}

   if (filterConfig?.length) {
    const payload = await createFilterPayload(
     filterConfig,
     filterFormData
    )

    const {totalAppliedFilters, ...restPayload} = payload

    setFilterCount(
     typeof totalAppliedFilters === 'number'
      ? totalAppliedFilters
      : Number(totalAppliedFilters ?? 0)
    )

    finalPayload = {
     ...restPayload,
    }
   }

   if (filterFormData?.status) {
    finalPayload.status = filterFormData.status
   }

   applyFilters(finalPayload)
  }

  void applyDynamicFilters()
 }, [filterFormData, filterConfig])
 useEffect(() => {
  if (!isPolling) return

  const interval = setInterval(async () => {
   if (pollingRef.current) return

   try {
    pollingRef.current = true

    await reload()

    const hasOngoing = tableData?.some(
     (item) => item.status === 'ONGOING'
    )

    if (!hasOngoing) {
     setIsPolling(false)
    }
   } finally {
    pollingRef.current = false
   }
  }, 3000)

  return () => clearInterval(interval)
 }, [isPolling, tableData, reload])

 useEffect(() => {
  const uploadData = location.state?.uploadPayload

  if (!location.state?.startUpload || !uploadData) {
   return
  }

  const uploadFile = async () => {
   try {
    setLoader(true)

    await bulkUploadApi(
     'farmer-bulk-upload',
     uploadData.file,
     uploadData.aggregatorKey,
     uploadData.parentId
    )

    setIsPolling(true)

    setTimeout(async () => {
     await reload()
    }, 1000)
   } catch (error) {
    setFailureMessage('Something went wrong')
    setShowFailureModal(true)
   } finally {
    setLoader(false)

    navigate(location.pathname, {
     replace: true,
     state: {},
    })
   }
  }

  void uploadFile()
 }, [location.state])

 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   <div className='bg-white rounded-lg'>
    <div className='flex justify-between items-center mb-6'>
     <h2 className='text-xl font-semibold'>
      {t('bulkUpload.title')}
     </h2>

     <button
      className='bg-[#4E83E4] text-white px-4 py-2 rounded-lg'
      onClick={handleBulkUploadBtn}
     >
      + {t('bulkUpload.bulkUploadManagement')}
     </button>
    </div>
    <div className='flex flex-wrap items-start lg:items-center gap-4 bg-[#F5F8FA] p-3 rounded-lg'>
     <div className='w-full lg:flex-1 bg-white min-w-[250px]'>
      <KTSearchBox
       label={t('bulkUpload.searchLabel')}
       placeholder={t('bulkUpload.search')}
       setSearchTerm={setSearchTerm}
      />
     </div>

     <div className='flex flex-wrap items-center gap-2 w-full lg:w-auto'>
      <span className='mr-2 text-sm text-[#333]'>
       {t('bulkUpload.quickFilter')}
      </span>

      {filters.map((f) => (
       <div
        key={f}
        onClick={() => applyStatusFilter(f)}
        className={`px-3 py-1.5 rounded-lg border cursor-pointer text-sm mr-2
                ${
                 selectedFilter === f
                  ? 'bg-[#4E83E4] text-white border-[#4E83E4]'
                  : 'bg-white border-[#CED8E0]'
                }`}
       >
        {f}
       </div>
      ))}
      {filterConfig?.length > 0 && (
       <div className='flex items-center justify-end'>
        <KTFilterBox
         parentId='bulk_upload_management'
         btnText={t(
          'userManagement.pendingUserAction.filters'
         )}
         containerClass='!bg-transparent !border-none !mt-0 !mb-0 !p-0 flex items-center'
         filterConfig={filterConfig}
         filterFormData={filterFormData ?? {}}
         setFilterFormData={(data) => {
          setFilterFormData(data)
          setCurrentPage(1)
         }}
         filterCount={filterCount}
         setLoader={setLoader}
         showDateRangeBox={false}
        />
       </div>
      )}
     </div>
    </div>
    <div className='mt-6'>
     <KTTable
      data={tableData}
      columns={columns}
      headerClass='bg-[#80868C] text-white'
      rowClass='even:bg-[#F5F8FA]'
      colClass='text-sm'
      showPagination={false}
     />
    </div>
    <div className='mt-4'>
     <KTPagination
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={totalRecord}
      onPageChange={setCurrentPage}
      setItemsPerPage={(size) => {
       setItemsPerPage(size)
       setCurrentPage(1)
      }}
     />
    </div>
    {showDownloadModal && (
     <DynamicModal
      title={t('bulkUpload.downloadConfirm')}
      description={t('bulkUpload.downloadDescription')}
      onConfirm={handleDownload}
      onCancel={() => {
       setShowDownloadModal(false)
       setSelectedRecord(null)
      }}
      textConfirm={t('bulkUpload.download')}
      textCancel={t('bulkUpload.cancel')}
     />
    )}
   </div>
   {showFailureModal && (
    <DynamicModal
     title='Upload Failed'
     description={failureMessage}
     onConfirm={() => {
      setShowFailureModal(false)
     }}
     onCancel={() => {
      setShowFailureModal(false)
     }}
     textConfirm='Okay'
     textCancel='Close'
    />
   )}
  </>
 )
}

export default BulkUploadManagement
